import { useEffect, useState, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { Grid } from '@material-ui/core';
import { useStyles, ModalSubmitButton, ModalCancelButton } from './style/style';
import { Checkbox } from '@mui/material';
import { getOutsidePartnersById } from '../../../apicalls/construction';
import { checkResData } from '../../../utils/common';
import { useSnackbar } from 'notistack';
import { isLoggedIn } from '../../../GlobalState';

type TProps = {
  constructId: number;
  folderId: number;
  folderName: string;
  setOpenFileAuthorityModal: React.Dispatch<React.SetStateAction<boolean>>;
  getAuthorizedOutsidePartner: (folderId: number) => Promise<any>;
  updateFolderAuthority: (
    folderId: number,
    outsidePartnerId: number[]
  ) => Promise<any>;
};

export const FolderAuthorityModal = (props: TProps) => {
  const {
    constructId,
    folderId,
    folderName,
    setOpenFileAuthorityModal,
    getAuthorizedOutsidePartner,
    updateFolderAuthority
  } = props;

  // 工事にアサインされている社外パートナーのリスト
  const [outsidePartners, setOutsidePartners] = useState([]);
  // 閲覧権限を付与する社外パートナーのリスト
  const [authorizedPartnerIds, setAuthorizedPartnerIds] = useState<number[]>(
    []
  );

  // 全員に公開・非公開のステート
  const [allChecked, setAllChecked] = useState(false);
  const [nobodyChecked, setNobodyChecked] = useState(false);

  // エラーハンドリング用
  const [loggedIn, setLoggedIn] = useRecoilState(isLoggedIn);
  const changeLoggedInData = useCallback(
    (newData: string) => setLoggedIn(newData),
    [setLoggedIn]
  );
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    // 工事にアサインされている社外パートナーを取得する
    getOutsidePartnersById(constructId)
      .then(data => {
        const { data: responseData } = data;
        const { outside_partner_list: outsidePartnerList } = responseData || {};

        if (outsidePartnerList) {
          checkResData(data, enqueueSnackbar, changeLoggedInData);
          setOutsidePartners(outsidePartnerList);
        } else {
          // エラーハンドリング: データが存在しない場合
          enqueueSnackbar('社外パートナーのリストを取得できませんでした', {
            variant: 'error'
          });
        }
      })
      .catch(error => {
        enqueueSnackbar('エラーが発生しました: ' + error.message, {
          variant: 'error'
        });
      });

    // すでに閲覧権限を付与されている社外パートナーを取得する
    getAuthorizedOutsidePartner(folderId)
      .then((data: any) => {
        const { data: responseData } = data;

        if (responseData) {
          checkResData(data, enqueueSnackbar, changeLoggedInData).then(res => {
            setAuthorizedPartnerIds(res.data);
          });
        } else {
          // エラーハンドリング: データが存在しない場合
          enqueueSnackbar(
            '閲覧権限を付与されている社外パートナーのリストを取得できませんでした',
            { variant: 'error' }
          );
        }
      })
      .catch(error => {
        enqueueSnackbar('エラーが発生しました: ' + error.message, {
          variant: 'error'
        });
      });
  }, []);

  // 権限を付与する社外パートナーのリストを監視して、
  // 全員に公開・非公開のステートを変更する
  useEffect(() => {
    setAllChecked(authorizedPartnerIds.length === outsidePartners.length);
    setNobodyChecked(authorizedPartnerIds.length === 0);
  }, [authorizedPartnerIds, outsidePartners]);

  const classes = useStyles();

  // 各社外パートナーのチェックボックスが押された時の処理
  const handleClickCheckbox = (id: number) => {
    setAuthorizedPartnerIds(prev =>
      prev.includes(id)
        ? prev.filter(partnerId => partnerId !== id)
        : [...prev, id]
    );
  };

  // 全員に公開のチェックボックスが押された時の処理
  const handleClickAllChecked = () => {
    if (!allChecked) {
      // 全ての社外パートナーIDを権限付与のリストに追加
      setAuthorizedPartnerIds(
        outsidePartners.map((partner: any) => partner.id)
      );
    }
    setAllChecked(!allChecked);
  };

  // 全員に非公開のチェックボックスが押された時の処理
  const handleClickNobodyChecked = () => {
    if (!nobodyChecked) {
      // 権限付与のリストを空にする
      setAuthorizedPartnerIds([]);
    }
    setNobodyChecked(!nobodyChecked);
  };

  // 変更ボタンが押された時の処理
  const handleClickChangeButton = () => {
    updateFolderAuthority(folderId, authorizedPartnerIds).then(res => {
      checkResData(res, enqueueSnackbar, changeLoggedInData);
      setOpenFileAuthorityModal(false);
    });
  };

  return (
    <div
      className={classes.paper}
      style={{
        width: '50%',
        maxHeight: '60%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <p style={{ marginTop: 20, marginBottom: 5 }}>
          公開する社外パートナーを設定
        </p>
        <p>フォルダ名: {folderName}</p>
      </div>
      {/* 全員に公開・非公開チェックボックス */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 10%' }}>
        <Checkbox checked={allChecked} onClick={handleClickAllChecked} />
        <div style={{ marginLeft: 5, marginRight: 40 }}>全員に公開</div>
        <Checkbox checked={nobodyChecked} onClick={handleClickNobodyChecked} />
        <div style={{ marginLeft: 5 }}>全員に非公開</div>
      </div>
      {/* 各社外パートナー表示部分 */}
      <div
        style={{
          overflowY: 'scroll',
          margin: '0 10%',
          border: 'solid',
          borderColor: 'gray'
        }}
      >
        {outsidePartners.map((partner: any, i: number) => (
          <Grid
            container
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              backgroundColor: i % 2 === 0 ? '#F5F5F5' : '#FFFFFF'
            }}
          >
            <Grid item xs={2} />
            <Grid item xs={1}>
              <Checkbox
                checked={authorizedPartnerIds.includes(partner.id)}
                onClick={() => handleClickCheckbox(partner.id)}
              />
            </Grid>
            <Grid item xs={7}>
              <div>
                {partner.name}
                {partner.vendor_name ? (
                  <span style={{ marginLeft: 10 }}>
                    ({partner.vendor_name})
                  </span>
                ) : (
                  ''
                )}
              </div>
            </Grid>
            <Grid item xs={2} />
          </Grid>
        ))}
      </div>
      {/* 下部ボタン */}
      <div style={{ textAlign: 'center', margin: '5%' }}>
        <ModalCancelButton
          variant='contained'
          onClick={() => {
            setOpenFileAuthorityModal(false);
          }}
        >
          キャンセル
        </ModalCancelButton>
        <ModalSubmitButton
          variant='contained'
          onClick={handleClickChangeButton}
        >
          変更する
        </ModalSubmitButton>
      </div>
    </div>
  );
};
